import DashboardCard from '../DashboardCard/DashboardCard';
import { asMoneyInKNotation, asMoneyWithoutZeros, getExponent } from 'helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { ProgressBar, SkeletonizedValue } from '@round/ui-kit';
import styles from './CreditLimits.module.css';
import { toDecimalPoint } from '@round/utils';
import uniq from 'lodash/uniq';
import { Banner } from '@round/whitelabel-ui';
import cn from 'classnames';
import { isNumber } from 'utility/utility';
import useCreditLimitsData, { CreditLimitData } from './useCreditLimitsData';
import { HorizontalVirtualList } from '@round/whitelabel-ui';
import range from 'lodash/range';
import { SearchInput } from '@round/whitelabel-ui';
import useCreditLimitsUrlState from './useCreditLimitsUrlState';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import useEffectWithPreviousDeps from 'Hooks/useEffectWithPreviousDeps';
import { Tooltip } from '@round/whitelabel-ui';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import { CountRangeLabel } from '@round/whitelabel-ui';

type Props = {
    teamId: number | null | undefined;
    onTeamChange: (teamId: number | null | undefined) => void;
};

const PAGE_SIZE = 10;
const MAX_PAGES = 3;

const CreditLimits = ({ teamId, onTeamChange }: Props) => {
    const featureFlags = useFeatureFlags();
    const isDetailedCreditLimitsEnabled = featureFlags.isEnabled('creatorbase_detailed_credit_limits');

    const { selectedClientId } = useClientSelect();
    const [urlState, setUrlState] = useCreditLimitsUrlState();

    useEffectWithPreviousDeps(
        (prev) => {
            const [prevClientId] = prev ?? [];
            if (prevClientId && prevClientId !== selectedClientId) {
                setUrlState({ clSearch: '' });
                onTeamChange(null);
            }
        },
        [selectedClientId, setUrlState, onTeamChange]
    );

    const {
        data: teamLimits,
        pageParams,
        count,
        isLoading,
        isFetching,
        hasNextPage,
        fetchNextPage,
        hasPreviousPage,
        fetchPreviousPage,
        errorMessage,
        isFetchingPreviousPage,
        isFetchingNextPage,
    } = useCreditLimitsData(
        {
            page_size: PAGE_SIZE,
            search: urlState.clSearch,
            client_id: selectedClientId,
        },
        { maxPages: MAX_PAGES, enabled: typeof selectedClientId === 'number' }
    );

    const currencies = uniq(teamLimits?.map((t) => t.currencyId) ?? []);
    const hasOnlyOneCurrency = currencies.length === 1;
    const shouldShowAggregate = teamLimits.length > 1 && hasOnlyOneCurrency && isNumber(count) && count <= PAGE_SIZE;

    const aggregatedData: CreditLimitData = {
        title: 'Total (All Teams)',
        teamId: null,
        creditUsed: teamLimits?.reduce((acc, data) => acc + data.creditUsed, 0) ?? 0,
        maxCredit: teamLimits.every((t) => t.maxCredit === null)
            ? null
            : teamLimits?.reduce((acc, data) => acc + (data.maxCredit ?? 0), 0) ?? 0,
        currencyId: currencies[0],
        unallocatedCampaignsCount:
            teamLimits?.reduce<number | null>((acc, data) => {
                if (acc === null && data.unallocatedCampaignsCount === null) {
                    return acc;
                }

                return (acc ?? 0) + (data.unallocatedCampaignsCount ?? 0);
            }, null) ?? null,
        livePostsCreditUsed: teamLimits?.reduce((acc, data) => acc + (data.livePostsCreditUsed ?? 0), 0) ?? 0,
        livePostsCampaignCount: teamLimits?.reduce((acc, data) => acc + (data.livePostsCampaignCount ?? 0), 0) ?? 0,
        allApprovedCreditUsed: teamLimits?.reduce((acc, data) => acc + (data.allApprovedCreditUsed ?? 0), 0) ?? 0,
        allApprovedCampaignCount: teamLimits?.reduce((acc, data) => acc + (data.allApprovedCampaignCount ?? 0), 0) ?? 0,
    };

    const dataWithAggregate = shouldShowAggregate ? [aggregatedData, ...teamLimits] : teamLimits;
    const emptyRows = range(3).map(() => undefined);
    const rows = isLoading
        ? emptyRows
        : isFetchingNextPage
          ? [...dataWithAggregate, ...emptyRows]
          : isFetchingPreviousPage
            ? [...emptyRows, ...dataWithAggregate]
            : dataWithAggregate;

    return (
        <DashboardCard className={styles.container}>
            <div className={styles.header}>
                <DashboardCard.Title>Credit Limits</DashboardCard.Title>

                <CountRangeLabel
                    pageParams={pageParams as number[]}
                    count={count}
                    pageSize={PAGE_SIZE}
                    isLoading={isLoading}
                />
            </div>

            <SearchInput
                className={styles.searchInput}
                value={urlState.clSearch}
                onChange={(search) => setUrlState({ clSearch: search })}
            />

            <HorizontalVirtualList
                className={cn(styles.creditLimits, { [styles.detailedCreditLimits]: isDetailedCreditLimitsEnabled })}
                data={rows}
                options={{ count: rows.length, estimateSize: () => 400, gap: 8, overscan: PAGE_SIZE }}
                onEndReached={(virtualizer) => {
                    if (hasNextPage && !isFetching && !isLoading) {
                        fetchNextPage().then(() => {
                            virtualizer.scrollToIndex(PAGE_SIZE * (MAX_PAGES - 1), { align: 'end' });
                        });
                    }
                }}
                onStartReached={(virtualizer) => {
                    if (hasPreviousPage && !isFetching && !isLoading) {
                        fetchPreviousPage().then(() => {
                            virtualizer.scrollToIndex(PAGE_SIZE, { align: 'start' });
                        });
                    }
                }}
            >
                {(data) => (
                    <CreditLimit
                        key={data?.teamId}
                        isActive={data?.teamId === teamId || (!isNumber(data?.teamId) && shouldShowAggregate)}
                        isLoading={!data?.title && (isFetching || isLoading)}
                        data={data}
                        onClick={() => onTeamChange(data?.teamId === teamId ? null : data?.teamId)}
                    />
                )}
            </HorizontalVirtualList>

            {errorMessage && (
                <Banner appearance="destructive" className={styles.errorBanner}>
                    {errorMessage}
                </Banner>
            )}
        </DashboardCard>
    );
};

export default CreditLimits;

type CreditLimitCardProps = { isLoading: boolean; data?: CreditLimitData } & Partial<{
    onClick: () => void;
    isActive?: boolean;
}>;

const CreditLimit = ({ data, isLoading, onClick, isActive }: CreditLimitCardProps) => {
    const featureFlags = useFeatureFlags();
    const isDetailedCreditLimitsEnabled = featureFlags.isEnabled('creatorbase_detailed_credit_limits');

    const { getCurrencyById } = useCurrencies();
    const currency = getCurrencyById(data?.currencyId);
    const percentUsed = isLoading || !data?.maxCredit ? 0 : data?.creditUsed! / data?.maxCredit;
    const campaignsCount = data?.unallocatedCampaignsCount;

    const hasNoCreditLimit = data?.maxCredit === null;

    return (
        <div className={cn(styles.card, { [styles.active]: isActive })} onClick={onClick}>
            <SkeletonizedValue isInitialized={!isLoading} width="75%">
                <p className={styles.cardTitle}>{data?.title ?? '-'}</p>
            </SkeletonizedValue>
            {isDetailedCreditLimitsEnabled ? (
                <>
                    <p className={styles.cardLabel}>Credit Used</p>
                    <div className={styles.creditBreakdown}>
                        {/* Credit used (Live posts)*/}
                        <p className={styles.cardSubLabel}>Live Posts</p>
                        <SkeletonizedValue isInitialized={!isLoading} width="5.5rem">
                            <Tooltip.Root>
                                {(anchor, setAnchor) => (
                                    <>
                                        <span ref={setAnchor} className={styles.creditUsed}>
                                            {asMoneyInKNotation({
                                                amount: data?.livePostsCreditUsed,
                                                currency,
                                                threshold: 5,
                                            })}
                                        </span>
                                        <Tooltip
                                            anchorElement={anchor}
                                            disabled={
                                                !data?.livePostsCreditUsed ||
                                                getExponent(data?.livePostsCreditUsed) <= 5
                                            }
                                        >
                                            <Tooltip.Body>
                                                <span>{asMoneyWithoutZeros(data?.livePostsCreditUsed, currency)}</span>
                                            </Tooltip.Body>
                                        </Tooltip>
                                    </>
                                )}
                            </Tooltip.Root>
                        </SkeletonizedValue>
                        <SkeletonizedValue isInitialized={!isLoading} width="5.5rem">
                            <span className={styles.campaignCount}>
                                {data?.livePostsCampaignCount
                                    ? `Across ${data?.livePostsCampaignCount} campaigns`
                                    : '-'}
                            </span>
                        </SkeletonizedValue>
                        {/* Credit used (All approved campaigns) */}
                        <p className={styles.cardSubLabel}>Approved Campaigns</p>
                        <SkeletonizedValue isInitialized={!isLoading} width="5.5rem">
                            <Tooltip.Root>
                                {(anchor, setAnchor) => (
                                    <>
                                        <span ref={setAnchor} className={styles.creditUsed}>
                                            {asMoneyInKNotation({
                                                amount: data?.allApprovedCreditUsed,
                                                currency,
                                                threshold: 5,
                                            })}
                                        </span>

                                        <Tooltip
                                            anchorElement={anchor}
                                            disabled={
                                                !data?.allApprovedCreditUsed ||
                                                getExponent(data?.allApprovedCreditUsed) <= 5
                                            }
                                        >
                                            <Tooltip.Body>
                                                <span>
                                                    {asMoneyWithoutZeros(data?.allApprovedCreditUsed, currency)}
                                                </span>
                                            </Tooltip.Body>
                                        </Tooltip>
                                    </>
                                )}
                            </Tooltip.Root>
                        </SkeletonizedValue>
                        <SkeletonizedValue isInitialized={!isLoading} width="5.5rem">
                            <span className={styles.campaignCount}>
                                {data?.allApprovedCampaignCount
                                    ? `Across ${data?.allApprovedCampaignCount} campaigns`
                                    : '-'}
                            </span>
                        </SkeletonizedValue>
                        <span className={styles.cardLabel}>Credit Limit:</span>{' '}
                        <SkeletonizedValue isInitialized={!isLoading} width="5.5rem">
                            <Tooltip.Root>
                                {(anchor, setAnchor) => (
                                    <>
                                        <span ref={setAnchor}>
                                            {hasNoCreditLimit
                                                ? 'No Limit'
                                                : asMoneyInKNotation({
                                                      amount: data?.maxCredit,
                                                      currency,
                                                      threshold: 5,
                                                  })}
                                        </span>

                                        <Tooltip
                                            anchorElement={anchor}
                                            disabled={
                                                hasNoCreditLimit || !data?.maxCredit || getExponent(data.maxCredit) <= 5
                                            }
                                        >
                                            <Tooltip.Body>
                                                <span>{asMoneyWithoutZeros(data?.maxCredit, currency)}</span>
                                            </Tooltip.Body>
                                        </Tooltip>
                                    </>
                                )}
                            </Tooltip.Root>
                        </SkeletonizedValue>
                    </div>
                </>
            ) : (
                <>
                    <SkeletonizedValue isInitialized={!isLoading} width="7rem">
                        <p className={styles.cardLabel}>
                            <span>Credit Used:</span>{' '}
                            <Tooltip.Root>
                                {(anchor, setAnchor) => (
                                    <>
                                        <span ref={setAnchor} className={styles.creditUsed}>
                                            {asMoneyInKNotation({ amount: data?.creditUsed, currency, threshold: 5 })}
                                        </span>
                                        <Tooltip
                                            anchorElement={anchor}
                                            disabled={!data?.creditUsed || getExponent(data.creditUsed) <= 5}
                                        >
                                            <Tooltip.Body>
                                                <span>{asMoneyWithoutZeros(data?.creditUsed, currency)}</span>
                                            </Tooltip.Body>
                                        </Tooltip>
                                    </>
                                )}
                            </Tooltip.Root>
                        </p>
                    </SkeletonizedValue>

                    <SkeletonizedValue isInitialized={!isLoading} width="7rem">
                        <p className={styles.cardLabel}>
                            <span>Credit Limit:</span>{' '}
                            <Tooltip.Root>
                                {(anchor, setAnchor) => (
                                    <>
                                        <span ref={setAnchor}>
                                            {hasNoCreditLimit
                                                ? 'No Limit'
                                                : asMoneyInKNotation({
                                                      amount: data?.maxCredit,
                                                      currency,
                                                      threshold: 5,
                                                  })}
                                        </span>
                                        <Tooltip
                                            anchorElement={anchor}
                                            disabled={
                                                hasNoCreditLimit || !data?.maxCredit || getExponent(data.maxCredit) <= 5
                                            }
                                        >
                                            <Tooltip.Body>
                                                <span>{asMoneyWithoutZeros(data?.maxCredit, currency)}</span>
                                            </Tooltip.Body>
                                        </Tooltip>
                                    </>
                                )}
                            </Tooltip.Root>
                        </p>
                    </SkeletonizedValue>
                </>
            )}
            <SkeletonizedValue isInitialized={!isLoading}>
                <ProgressBar
                    progress={Math.min(percentUsed * 100, 100)}
                    barClassName={styles.progressBar}
                    trackClassName={styles.progressBarTrack}
                />
            </SkeletonizedValue>

            <div className={styles.cardRow}>
                <SkeletonizedValue isInitialized={!isLoading} width="5rem">
                    <p className={styles.cardLabel}>
                        {hasNoCreditLimit ? '-' : `${toDecimalPoint(percentUsed * 100, 2)}% utilized`}
                    </p>
                </SkeletonizedValue>

                {isNumber(campaignsCount) && !isLoading ? (
                    <p className={styles.cardLabel}>
                        {campaignsCount} campaign{campaignsCount !== 1 ? 's' : ''}
                    </p>
                ) : null}
            </div>
        </div>
    );
};
